import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Space, Card, Typography, Popconfirm, message, Modal, Descriptions } from 'antd';
import { DollarOutlined, CreditCardOutlined, WalletOutlined, FileTextOutlined, UndoOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { paymentService, Payment, PaymentSummary } from '../../services/paymentService';

const { Title, Text } = Typography;

interface PaymentHistoryProps {
  entityType: 'estimate' | 'work_order';
  entityId: number;
  showActions?: boolean;
  showRefund?: boolean;
  onUpdate?: () => void;
}

const PaymentHistory: React.FC<PaymentHistoryProps> = ({
  entityType,
  entityId,
  showActions = false,
  showRefund = false,
  onUpdate
}) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);

  useEffect(() => {
    loadPayments();
  }, [entityType, entityId]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const result = await paymentService.getPaymentsForEntity(entityType, entityId);
      setPayments(result.payments);
      setSummary(result.summary);
    } catch (error) {
      console.error('Error loading payments:', error);
      message.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async (payment: Payment) => {
    try {
      await paymentService.processRefund(payment.id!, payment.amount, 'Refund requested');
      message.success('Refund processed successfully');
      loadPayments();
      if (onUpdate) onUpdate();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to process refund');
    }
  };

  const handleDelete = async (paymentId: number) => {
    try {
      await paymentService.deletePayment(paymentId);
      message.success('Payment deleted successfully');
      loadPayments();
      if (onUpdate) onUpdate();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to delete payment');
    }
  };

  const showDetails = (payment: Payment) => {
    setSelectedPayment(payment);
    setDetailsVisible(true);
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'card':
        return <CreditCardOutlined />;
      case 'cash':
        return <DollarOutlined />;
      case 'check':
        return <FileTextOutlined />;
      case 'affirm':
        return <WalletOutlined />;
      default:
        return <DollarOutlined />;
    }
  };

  const getPaymentMethodTag = (method: string) => {
    const colors: Record<string, string> = {
      card: 'blue',
      cash: 'green',
      check: 'orange',
      affirm: 'purple',
      paypal: 'cyan'
    };
    return (
      <Tag color={colors[method] || 'default'} icon={getPaymentMethodIcon(method)}>
        {method.toUpperCase()}
      </Tag>
    );
  };

  const getStatusTag = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'default',
      processing: 'processing',
      completed: 'success',
      failed: 'error',
      refunded: 'warning',
      partially_refunded: 'warning'
    };
    return <Tag color={colors[status] || 'default'}>{status.replace('_', ' ').toUpperCase()}</Tag>;
  };

  const columns = [
    {
      title: 'Payment #',
      dataIndex: 'payment_number',
      key: 'payment_number',
      render: (text: string, record: Payment) => (
        <Button type="link" onClick={() => showDetails(record)}>
          {text}
        </Button>
      )
    },
    {
      title: 'Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (date: string) => dayjs(date).format('MMM D, YYYY h:mm A')
    },
    {
      title: 'Method',
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (method: string) => getPaymentMethodTag(method)
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: Payment) => (
        <div>
          <Text strong>${amount.toFixed(2)}</Text>
          {record.processor_fee && record.processor_fee > 0 && (
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              Fee: ${record.processor_fee.toFixed(2)}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status: string) => getStatusTag(status)
    },
    ...(showActions
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Payment) => (
              <Space>
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => showDetails(record)}
                >
                  Details
                </Button>
                {showRefund && record.payment_status === 'completed' && (
                  <Popconfirm
                    title="Process Refund"
                    description="Are you sure you want to refund this payment?"
                    onConfirm={() => handleRefund(record)}
                    okText="Yes, Refund"
                    cancelText="No"
                  >
                    <Button type="link" size="small" icon={<UndoOutlined />} danger>
                      Refund
                    </Button>
                  </Popconfirm>
                )}
                {record.payment_method === 'cash' || record.payment_method === 'check' ? (
                  <Popconfirm
                    title="Delete Payment"
                    description="Are you sure you want to delete this payment record?"
                    onConfirm={() => handleDelete(record.id!)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link" size="small" icon={<DeleteOutlined />} danger>
                      Delete
                    </Button>
                  </Popconfirm>
                ) : null}
              </Space>
            )
          }
        ]
      : [])
  ];

  return (
    <>
      {summary && (
        <Card style={{ marginBottom: 16 }}>
          <Space direction="horizontal" size="large" style={{ width: '100%', justifyContent: 'space-around' }}>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Total Amount</Text>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                ${summary.total_amount.toFixed(2)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Amount Paid</Text>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                ${summary.amount_paid.toFixed(2)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Balance Due</Text>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: summary.balance_due > 0 ? '#ff4d4f' : '#52c41a' }}>
                ${summary.balance_due.toFixed(2)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Payment Status</Text>
              <div style={{ marginTop: 8 }}>
                {getStatusTag(summary.payment_status)}
              </div>
            </div>
          </Space>
        </Card>
      )}

      <Card title="Payment History">
        <Table
          columns={columns}
          dataSource={payments}
          rowKey="id"
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No payments recorded' }}
        />
      </Card>

      <Modal
        title={`Payment Details - ${selectedPayment?.payment_number}`}
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        {selectedPayment && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Payment Number">{selectedPayment.payment_number}</Descriptions.Item>
            <Descriptions.Item label="Date">{dayjs(selectedPayment.payment_date).format('MMMM D, YYYY h:mm A')}</Descriptions.Item>
            <Descriptions.Item label="Payment Method">{getPaymentMethodTag(selectedPayment.payment_method)}</Descriptions.Item>
            <Descriptions.Item label="Status">{getStatusTag(selectedPayment.payment_status)}</Descriptions.Item>
            <Descriptions.Item label="Amount">${selectedPayment.amount.toFixed(2)}</Descriptions.Item>
            {selectedPayment.processor_fee && selectedPayment.processor_fee > 0 && (
              <Descriptions.Item label="Processing Fee">${selectedPayment.processor_fee.toFixed(2)}</Descriptions.Item>
            )}
            {selectedPayment.net_amount && (
              <Descriptions.Item label="Net Amount">${selectedPayment.net_amount.toFixed(2)}</Descriptions.Item>
            )}
            {selectedPayment.check_number && (
              <Descriptions.Item label="Check Number">{selectedPayment.check_number}</Descriptions.Item>
            )}
            {selectedPayment.reference_number && (
              <Descriptions.Item label="Reference Number">{selectedPayment.reference_number}</Descriptions.Item>
            )}
            {selectedPayment.processor && (
              <Descriptions.Item label="Processor">{selectedPayment.processor.toUpperCase()}</Descriptions.Item>
            )}
            {selectedPayment.processor_payment_id && (
              <Descriptions.Item label="Transaction ID">{selectedPayment.processor_payment_id}</Descriptions.Item>
            )}
            {selectedPayment.refund_amount && selectedPayment.refund_amount > 0 && (
              <>
                <Descriptions.Item label="Refund Amount">${selectedPayment.refund_amount.toFixed(2)}</Descriptions.Item>
                <Descriptions.Item label="Refund Reason">{selectedPayment.refund_reason}</Descriptions.Item>
                <Descriptions.Item label="Refunded At">{dayjs(selectedPayment.refunded_at).format('MMMM D, YYYY h:mm A')}</Descriptions.Item>
              </>
            )}
            {selectedPayment.notes && (
              <Descriptions.Item label="Notes">{selectedPayment.notes}</Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default PaymentHistory;
